// Core Modules
import { ApplicationConfig, importProvidersFrom, provideZonelessChangeDetection } from '@angular/core';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { provideRouter, TitleStrategy, withInMemoryScrolling, withRouterConfig } from '@angular/router';

// External Modules
import { CacheFactory } from 'cachefactory';
import * as Highcharts from 'highcharts';
import { provideHighcharts } from 'highcharts-angular';
import { NgxPermissionsModule } from 'ngx-permissions';
import { provideUserIdleConfig } from 'angular-user-idle';

// `usage-chart.component.ts`'s theme sets `accessibility: { enabled: true }`, but the core
// package ships no accessibility behavior — the module must be loaded once, here, before any
// chart renders, or Highcharts silently drops the option and warns in the console instead.
// This is a side-effect-only import, not a value: the core UMD build above sets the shared
// `window._Highcharts` instance as it loads, and this module reads that global and registers
// itself against it purely by being executed — its own export is not a function to call.
import 'highcharts/modules/accessibility';

// Tokens
import { WINDOW } from '@core/token/tokens';

// Interceptors
import { authInterceptor } from '@core/interceptors/auth.interceptor';

// Strategies
import { CustomTitleStrategy } from '@core/strategies/custom-title-strategies';

// Routes
import { routes } from '@app/app.routes';

// Environment
import { environment } from '@env/environment';

export const appConfig: ApplicationConfig = {
  providers: [
    CacheFactory,
    provideZonelessChangeDetection(),
    provideRouter(
      routes,
      withInMemoryScrolling({
        scrollPositionRestoration: 'top',
      }),
      withRouterConfig({ onSameUrlNavigation: 'reload' })
    ),
    provideHttpClient(withInterceptors([authInterceptor])),
    provideHighcharts({ instance: () => Promise.resolve(Highcharts) }),
    importProvidersFrom(NgxPermissionsModule.forRoot()),
    provideUserIdleConfig({
      idle: environment.SESSION.maxIdleWaitTime,
      timeout: environment.SESSION.logoutNotifyExpiryTime,
      ping: environment.SESSION.refreshSessionInterval,
    }),
    {
      provide: TitleStrategy,
      useClass: CustomTitleStrategy,
    },
    {
      provide: WINDOW,
      useValue: window,
    },
  ],
};
